import type { TowerDesign } from "@/game/towerSpec";
import type { Material } from "@/physics/model";
import type { MountedLoad } from "@/game/appurtenances";
import { CATALOG } from "@/game/appurtenances";

/**
 * Newtown 185 ft monopole — a real Engineered Endeavors (EEI) pole on Berkshire
 * Rd, Newtown, Fairfield County CT (Crown Castle BU 806354). Rebuilt from the
 * stamped tnxTower structural analysis (Morrison Hershfield, 2023-01-04) in
 * reference_towers/02_engineering_drawings/CT_Newtown_BerkshireRd_EEI_185ft_monopole_structural.pdf.
 *
 * Real structure (Tapered Pole Section Geometry p.8, elevation E-1):
 *   - 18-sided tapered tube, 5 sections over 185 ft (56.39 m). Reinforced 2009.
 *   - Base OD 62.50" (1.588 m), 0.500" wall; tip OD 29.00" (0.737 m), 0.250" wall.
 *   - Grade A572-65 throughout (Fy 65 ksi / 448 MPa, Fu 80 ksi) — inline below.
 *   - Pole steel weight 41.2 kip ≈ 18.7 t (section weights sum, E-1).
 *
 * Design basis (p.3, E-1 notes): TIA-222-H, Risk Cat II, 116 mph ultimate 3-sec
 * gust, Exposure C, Topo 1; also 50 mph + 1.00" ice; service 60 mph. Factored
 * base reactions (116 mph): axial 80 kip (356 kN), shear 48 kip (214 kN),
 * overturning moment 6562 kip-ft (8.90 MN·m). Tower rating 75.1% (pole L4).
 *
 * Foundation & connection (Table 5): anchor rods 68.9%, base plate 80.2%, base
 * foundation structure 73.5%, and SOIL INTERACTION 91.0% — the ground is the
 * tightest link (all Pass). Modelled as a drilled pier on dense Fairfield County
 * glacial till, deliberately NOT oversized, so soil is near-critical as in the
 * report. Anchor-bolt base plate sized competent (not governing, matching the
 * 68.9% rod / 80.2% plate ratings).
 *
 * Game modelling (docs/TOWER_SPEC.md §7): moment tube → slim braced lattice + a
 * `shell`. This is a fat pole, so the internal 12-leg cage at 1.56 m across
 * (baseRadius 0.78) closely matches the true 1.588 m base; faces 12 rounds the
 * real 18-sided section; heavy 8" pipe legs stand in for the thick tube wall.
 *
 * Appurtenances: a dense 5-carrier stack (Tables 1-2) — main array at 177 ft
 * (CCI/KMW panels + Ericsson RRUS), and secondary arrays at 165/148/135 ft, over
 * three azimuths, plus the feed-line bundle.
 */

const A572_65: Material = { name: "A572 Gr65", E: 200e9, Fy: 448e6, rho: 7850 };
const AZ = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3];

// Real multi-carrier arrays (centre-line elevations, ft → m).
const arrays: MountedLoad[] = [
  // 177 ft — main: 6 CCI OPA + 3 KMW panels, ~12 Ericsson RRUS radios
  ...AZ.flatMap((azimuth) => [
    { appurtenance: CATALOG.panelSector, height: 53.95, azimuth },
    { appurtenance: CATALOG.panelSector, height: 53.95, azimuth },
    { appurtenance: CATALOG.panelSector, height: 53.95, azimuth },
    { appurtenance: CATALOG.rru, height: 53.95, azimuth },
    { appurtenance: CATALOG.rru, height: 53.95, azimuth },
    { appurtenance: CATALOG.rru, height: 53.95, azimuth },
    { appurtenance: CATALOG.rru, height: 53.95, azimuth },
  ]),
  // 165 ft — Ericsson AIR6449 + RFS/Celwave panels + radios
  ...AZ.flatMap((azimuth) => [
    { appurtenance: CATALOG.panelSector, height: 50.29, azimuth },
    { appurtenance: CATALOG.panelSector, height: 50.29, azimuth },
    { appurtenance: CATALOG.rru, height: 50.29, azimuth },
  ]),
  // 148 ft — Ericsson AIR21 + CommScope panels
  ...AZ.flatMap((azimuth) => [
    { appurtenance: CATALOG.panelSector, height: 45.11, azimuth },
    { appurtenance: CATALOG.panelSector, height: 45.11, azimuth },
  ]),
  // 135 ft — JMA + Fujitsu radios
  ...AZ.map((azimuth) => ({ appurtenance: CATALOG.panelSmall, height: 41.15, azimuth })),
  // feed-line bundle (12×1-5/8" + 6×5/8" + …), base → main array
  { appurtenance: CATALOG.coaxBundle, height: 53.95, runLength: 53.95 },
  { appurtenance: CATALOG.coaxBundle, height: 53.95, runLength: 53.95 },
];

export const design: TowerDesign = {
  id: "monopole-newtown-185ft",
  name: "Monopole (Newtown 185 ft, EEI)",
  description: "Real 185 ft EEI A572-65 monopole, Newtown CT — soil-critical (Crown Castle BU 806354).",
  author: "reference: CT Newtown Berkshire Rd EEI 185 ft tnxTower SA Rev 0",
  spec: {
    type: "monopole",
    height: 56.39, // 185 ft
    stack: {
      faces: 12, // rounds the real 18-sided pole
      panels: 18, // 3.13 m panels
      baseRadius: 0.78, // 1.56 m across ≈ real 1.588 m base OD
      topRadius: 0.36, // 0.72 m ≈ real 0.737 m tip
      legSection: "legXXL", // 8" Sch-40 pipe — thick tube wall stand-in
      braceSection: "braceHeavy", // L4x4 diagonals — ample for the per-face panel shear
      legMaterial: A572_65,
      braceMaterial: A572_65,
      bracing: "X",
    },
    shell: {
      baseDiameter: 1.588, // real EEI base OD, 18-sided
      topDiameter: 0.737, // real tip OD
    },
    foundation: {
      soil: "dense-sand", // dense Fairfield County glacial till — soil governs at 91% in the report
      base: {
        type: "pier",
        width: 3.0,
        diameter: 3.0, // ~10 ft drilled pier for the 8.9 MN·m base moment
        depth: 8.5, // ~28 ft embedment
        concrete: "c30",
      },
      connection: {
        kind: "anchorBolts",
        nBolts: 16, // large anchor ring for a 62.5" base
        bolt: "b2_0", // 2" rods
        grade: "f1554_105", // F1554 Gr.105
        boltCircleDia: 1.78, // ~70" bolt circle
        embedment: 0.5,
        edgeDistance: 0.35,
        platePlanArea: 2.6, // ~64" square base plate
        plateThickness: 0.064, // ~2.5"
        plateGrade: "a36rod",
        fc: 30e6,
      },
    },
    loads: arrays,
  },
};
